import type { JourneyState } from "src/pages/journeys/types";
import type { TypedMessage } from "src/cep/messages/types";
import type { TimezoneMode } from "src/cep/campaigns/campaign-detail/form-types";
import type { DerivedCampaignStatus } from "src/graphql";
import { createTypedOutlet } from "src/router";
import type {
  ChannelType,
  CepEmailSenderConfig,
  CepSenderConfig,
  CepSmsSenderConfig,
} from "src/types/cep";
import type { Condition } from "src/types/visual";

/**
 * Pick only the fields that message detail pages (overview, content)
 * actually consume. This keeps the outlet compatible with both the standalone
 * message loader (GetMessageByIdQuery) and the campaign message loader
 * (GetCampaignByIdQuery) without unsafe casts.
 *
 * `OutletMessage` is a discriminated union by `channel_type` so leaf components
 * read an already-narrowed `cep_sender_identity.config` (CepEmailSenderConfig
 * for email, CepSmsSenderConfig for sms) without any per-site jsonb casts. The
 * single boundary cast lives in the loaders that build the outlet context.
 */
type OutletMessageBase = Pick<
  TypedMessage,
  | "id"
  | "name"
  | "cep_brand_id"
  | "send_config"
  | "send_gates"
  | "transactional"
  | "measurement_config"
  | "targeting_config"
  | "current_content"
  | "current_metadata"
  | "content_dynamic_slot_usages"
  | "draft_content"
  | "draft_version"
  | "published_metadata_version"
  | "draft_metadata_version"
  | "cep_subscription_topic"
  | "message_push_apps"
  | "push_config"
  | "push_config_id"
  | "cep_device_preview_id"
  | "llm_chat_session_id"
  | "artifact_id"
  | "llm_chat_session"
>;

type SenderIdentityRow = NonNullable<TypedMessage["cep_sender_identity"]>;
type SenderIdentityWithConfig<C> =
  | (Omit<SenderIdentityRow, "config"> & { config: C | null })
  | null;

export type OutletMessageEmail = OutletMessageBase & {
  channel_type: ChannelType.Email;
  cep_sender_identity: SenderIdentityWithConfig<CepEmailSenderConfig>;
};
export type OutletMessageSms = OutletMessageBase & {
  channel_type: ChannelType.Sms;
  cep_sender_identity: SenderIdentityWithConfig<CepSmsSenderConfig>;
};
export type OutletMessageWhatsapp = OutletMessageBase & {
  channel_type: ChannelType.Whatsapp;
  cep_sender_identity: SenderIdentityWithConfig<CepSmsSenderConfig>;
};
export type OutletMessagePush = OutletMessageBase & {
  channel_type: ChannelType.Push;
  cep_sender_identity: SenderIdentityWithConfig<CepSenderConfig>;
};
export type OutletMessageInApp = OutletMessageBase & {
  channel_type: ChannelType.InApp;
  cep_sender_identity: SenderIdentityWithConfig<CepSenderConfig>;
};
export type OutletMessageUnknown = OutletMessageBase & {
  channel_type: null;
  cep_sender_identity: SenderIdentityWithConfig<CepSenderConfig>;
};

export type OutletMessage =
  | OutletMessageEmail
  | OutletMessageSms
  | OutletMessageWhatsapp
  | OutletMessagePush
  | OutletMessageInApp
  | OutletMessageUnknown;

export type JourneyScheduleInfo = {
  schedule: unknown;
  lastCompletedRunFinishedAt: string | null;
};

export type AudienceInfo = {
  id: string;
  name: string;
  /** Narrowed at the loader boundary from the segment's jsonb visual_query_filter. */
  conditions: Condition[];
};

export type CampaignInfo = {
  id: string;
  name: string;
  campaignType: string | null;
  scheduleConfig: unknown;
  /** Narrowed at the loader boundary from the campaign's timezone_mode column. */
  timezoneMode: TimezoneMode;
  derivedStatus: DerivedCampaignStatus | undefined;
  lastCompletedRun: {
    completedAt: string;
    totalTriggered: number;
  } | null;
};

export type MessageOutletContext = {
  message: OutletMessage;
  parentModel: { id: string; name: string } | null;
  /** Audience model ID for SMS preview queries. Comes from the campaign segment
   *  or the journey entry-cohort node, depending on the trigger type. */
  audienceModelId: string | null;
  /** Raw journey state for the message's trigger, when journey-attached. Policy
   *  (editability, content save mode) is derived from this via
   *  `useCurrentSurface`/`editability.ts`, not stored here. */
  journeyState: JourneyState | null;
  journeyInfo: {
    id: string;
    name: string;
    realtime: boolean;
    /** The journey tile that sends the message. */
    nodeId: string | null;
  } | null;
  campaignInfo: CampaignInfo | null;
  audienceInfo: AudienceInfo | null;
  journeyScheduleInfo: JourneyScheduleInfo | null;
};

const {
  OutletProvider: MessageOutlet,
  ContextProvider: MessageOutletContextProvider,
  useContext: useMessageOutletContext,
  useOptionalContext: useMessageOutletContextIfAvailable,
} = createTypedOutlet<MessageOutletContext>("Message");

export {
  MessageOutlet,
  MessageOutletContextProvider,
  useMessageOutletContext,
  useMessageOutletContextIfAvailable,
};
